import { useMemo } from "react"

import { AssociateProductType } from "../../../types"

import type { CompetitorProduct, Product } from "@/graphql/codegen/graphql"
import { ComputedProductStatus } from "@/graphql/codegen/graphql"
import useProductsForAssociationList from "@/hooks/useProductsForAssociationList"
import useCompetitorProducts from "@/screens/ManageProducts/components/CompetitorProducts/hooks/useCompetitorProducts"

const ACTIVE_PRODUCT_STATUSES = [ComputedProductStatus.Active, ComputedProductStatus.PendingApproval]

interface UseEquivalentProductSearchParams {
  productType: AssociateProductType
  searchTerm: string
  productId?: number
  competitorProductId?: string
  showInactive: boolean
}

interface UseEquivalentProductSearchReturn {
  products: Product[] | CompetitorProduct[]
  loading: boolean
}

export const useEquivalentProductSearch = ({
  productType,
  searchTerm,
  productId,
  competitorProductId,
  showInactive,
}: UseEquivalentProductSearchParams): UseEquivalentProductSearchReturn => {
  const akrochemProductStatuses = productId || showInactive ? undefined : ACTIVE_PRODUCT_STATUSES

  const { products: akrochemProducts, fetchingProducts: akrochemFetching } = useProductsForAssociationList({
    query: productId ? undefined : searchTerm, // Only send query if no productId
    productIds: productId ? [productId] : undefined, // Only send productIds when user selected from dropdown
    computedProductStatuses: akrochemProductStatuses,
    pause: productType !== AssociateProductType.Akrochem,
  })

  const { competitorProductsList, competitorProductsListFetching } = useCompetitorProducts({
    query: competitorProductId ? "" : searchTerm,
    competitorProductId,
    pause: productType !== AssociateProductType.Competitor,
  })

  const result = useMemo(() => {
    if (productType === AssociateProductType.Akrochem) {
      return {
        products: akrochemProducts,
        loading: akrochemFetching,
      }
    }

    return {
      products: competitorProductsList,
      loading: competitorProductsListFetching,
    }
  }, [productType, akrochemProducts, akrochemFetching, competitorProductsList, competitorProductsListFetching])

  return result
}
